import _ from "csi/_base/underscore";
import AddPath from "csi/def/AddPath";
import { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import { View } from "csi/def/View";
import Widget from "csi/form/Widget";
import csi from "csi/main";
import { StoreNames } from "csi/store/Names";
import { C8Node, cnl } from "csi/typings";
import { stringSubstitute } from "csi/utility/stringUtility";
import { ViewWidget } from "csi/view/Widget";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";

const ANALYSIS_TYPE = "AnalysisType";
const FORMULATED_PRODUCT_PATH = "Child:Subtype/Child:Settings";
const COUNTRIES = "Countries";
const PARAMETER_VALUE_BASE_URL = "centric://REFLECTION/INSTANCE/ProductComplianceAnalysisType/";
const PARAMETER_TYPE = "ParameterType";
const PRODUCT_COMPLIANCE_ANALYSIS_PARAMETER_VALUE = "ProductComplianceAnalysisParameterValue";
const PRODUCT_PATH = "Child:ProductType/Child:ProductionConfig";
const FOOD = "Food";
const STYLE = "Style";
const MATERIAL = "Material";
const FORMULATED_MATERIAL = "FormulatedMaterial";
const USAGE = "Usage";

const FoodLabelComplianceAnalysisForm = declare(Widget, {
	/** @property {Field} _analysisTypeField - Field for AnalysisType */
	_analysisTypeField: null,

	/** @property {Object} defaultComplianceAnalysisUsages - The aggregated refmap on BaseProduct holding default usages */
	defaultComplianceAnalysisUsages: null,

	/** @property {boolean} hasSingleUsageOnBaseProduct - True if the parentNode is a BaseProduct (excluding shape) with a single default usage */
	hasSingleUsageOnBaseProduct: false,

	/** @property {C8Node} parentProductNode - The node of the BaseProduct */
	parentProductNode: null,


	/**
	 * @override
	 * Set "Product" field value to the Food label's parent product
	 */
	_resolveView(): void {
		this._analysisTypeField = this.getField(ANALYSIS_TYPE);

		const ctx = this.getContext();
		const foodLabelRev = ctx.getNode();
		if (foodLabelRev) {
			this.parentProductNode = ctx.cache.getPathNode(foodLabelRev, "Child:ParentProduct");
			this.addFixedValue("Product", this.parentProductNode?.$Name);
			this._resolveViewPath(this, this.parentProductNode);
			this.defaultComplianceAnalysisUsages = this.parentProductNode?.DefaultComplianceAnalysisUsages;
		}

		this._analysisTypeField.m_onChange = "_onChangeFromAnalysisType";

		// set default value for PercentCalculationType
		this.addDefaultValue("PercentCalculationType", xml.enumCreate("FlatIngredientsPercentCalculationTypes", "RelativeQty"));

		this.inherited(arguments);
	},

	/**
	 * Substitute correct path based on Product vs. FormulatedProduct
	 */
	_resolveViewPath(view: ViewWidget, product: C8Node): void {
		const defView = view?.defView;
		const analysisTypeField = defView.getField(ANALYSIS_TYPE);

		analysisTypeField?.m_relationship?.m_addPaths?.forEach((path) => {
			const PATH = product.$Type === "FormulatedMaterial" || product.$Type === "Food" ? FORMULATED_PRODUCT_PATH : PRODUCT_PATH;
			path.m_path = stringSubstitute(path.m_path, [PATH]);
		});
	},

	/**
	 * @override
	 * Disable the Countries and Usage fields conditionally
	 */
	_updateFieldVisibility(field: Field): void {
		// Form is being initialized
		if (!field && !this.hasSingleUsageOnBaseProduct) {
			this.setFieldDisabled(USAGE, true);
			this.setFieldDisabled(COUNTRIES, true);
		}
		// AnalysisType field was modified
		else if (field && field.idParameter === ANALYSIS_TYPE) {
			const disableFields = this.getFieldValue(ANALYSIS_TYPE) === csi.EMPTY_URL;
			this.setFieldDisabled(USAGE, disableFields);
			this.setFieldDisabled(COUNTRIES, disableFields);
		}
	},

	/**
	 * @override
	 * Initialize the default field values
	 */
	onViewStartBuild(): void {
		if (this.hasSingleUsageInRefMap(this.defaultComplianceAnalysisUsages)) {
			const requestAnalysisType = this.getSingleUsageFromRefMap(this.defaultComplianceAnalysisUsages);

			if (this.isAnalysisTypeOnBaseProduct(requestAnalysisType)) {
				const usageParameterValueCNL = this.defaultComplianceAnalysisUsages[requestAnalysisType];
				const analysisTypeCNL = PARAMETER_VALUE_BASE_URL + requestAnalysisType;

				this.addDefaultValue(ANALYSIS_TYPE, analysisTypeCNL);
				this.addDefaultValue(USAGE, usageParameterValueCNL);
				this._setDefaultCountriesValues(analysisTypeCNL);

				this.hasSingleUsageOnBaseProduct = true;

				this.inherited(arguments);
				return;
			}
		}

		this.inherited(arguments);
	},

	/**
	 * Path from parentProduct to the ProductSalesRegionSpec's DefaultComplianceAnalysisCountries
	 * @returns 
	 */
	_getPathToSalesRegionDefaultCountries(): string {
		const salesRegionOwnerProductTypes = [FOOD, STYLE, FORMULATED_MATERIAL];
		let pathToSalesRegionDefaultCountries: string;
		if (salesRegionOwnerProductTypes.includes(this.parentProductNode?.$Type)) {
			pathToSalesRegionDefaultCountries = "Child:ProductSalesRegions/Child:ProductSalesRegionSpec/Child:DefaultComplianceAnalysisCountries";
		} else if (this.parentProductNode?.$Type === MATERIAL) {
			pathToSalesRegionDefaultCountries = "Child:MaterialProductSalesRegionSpecs/Child:DefaultComplianceAnalysisCountries";
		}

		return pathToSalesRegionDefaultCountries;
	},

	/**
	 * On form start, initialize the Countries field default values on the ParentProduct's ProductSalesRegionSpecs' DefaultComplianceAnalysisCountries.
	 * The countries should have AnalysisTypes includes the selected ProductComplianceAnalysisType
	 */
	_setDefaultCountriesValues(analysisTypeCNL: string): void {
		// Path from parentProduct to the ProductSalesRegionSpec's DefaultComplianceAnalysisCountries
		const pathToSalesRegionDefaultCountries = this._getPathToSalesRegionDefaultCountries();
		const countryNodes = _.uniq(this.getContext().cache.getPathNodes(this.parentProductNode, pathToSalesRegionDefaultCountries));
		if (!_.isEmpty(countryNodes)) {
			const values = [];
			_.each(countryNodes, (country) => {
				if (country.AnalysisTypes.includes(analysisTypeCNL)) {
					values.push(country.$URL);
				}
			});
			this.addDefaultValue(COUNTRIES, values);
		}
	},

	/**
	 * Disable/Enables the Countries and Usage fields and resets their values
	 */
	_onChangeFromAnalysisType(defView: View, field: Field, value: cnl): void {
		this.setFieldValue(COUNTRIES, csi.EMPTY_STRING);
		this.setFieldValue(USAGE, csi.EMPTY_URL);

		// Set countries field to suggested countries if available
		this.resetFieldStoreAndValue(COUNTRIES, []);
		this.resetFieldStoreAndValue(USAGE, []);

		// Get the cached results of the priority store query
		const defaultSelection = this.getField(COUNTRIES).getStore().priorityStore?._store?.data;
		// Process the list of records into a list of CNL strings
		let selectedURLs = defaultSelection?.map((node) => node.$);
		// Check if the selectedURLs list is empty
		if (_.isEmpty(selectedURLs) || selectedURLs[0] == "Empty_All Items") {
			selectedURLs = [];
		}
		// Select all the suggested countries
		this.setFieldValue(COUNTRIES, selectedURLs ? selectedURLs : null);
	},

	/**
	 * @override
	 * Get field store for Countries and Usages
	 */
	getFieldStore(field: Field, value: string): StoreNames {
		const analysisTypeFieldValue = this.getFieldValue(ANALYSIS_TYPE);

		if (analysisTypeFieldValue == csi.EMPTY_URL) {
			return this.inherited<StoreNames>(arguments);
		} else if (field.m_name === COUNTRIES) {
			return this.getCountriesStore(field, analysisTypeFieldValue);
		} else if (field.m_name === USAGE) {
			return this.getUsageStore(field, analysisTypeFieldValue);
		}

		return this.inherited<StoreNames>(arguments);
	},

	getCountriesStore(field: Field, analysisTypeFieldValue: string): StoreNames {
		const ctx = this.getContext();

		// limitPath will be set based on the parent product type
		const limitPath = this._getPathToSalesRegionDefaultCountries();
		const query = new Query()
			.Attr(PARAMETER_TYPE, xml.OP_EQUALS, "ProductComplianceAnalysisParameterType:Country")
			.RefAttr("AnalysisTypes", xml.OP_EQUALS, analysisTypeFieldValue);

		// Create Relationship for countries with Analysis Type (Preferred)
		const countryWithAnalysisTypeRel = lang.mixin(new Relationship(), {
			m_typeNode: PRODUCT_COMPLIANCE_ANALYSIS_PARAMETER_VALUE,
			m_query: query,
			m_url: this.parentProductNode.$URL,
			m_addPaths: [lang.mixin(new AddPath(), {
				m_path: limitPath,
			})],
		});

		// Create Relationship for all countries (Other)
		const countryRel = lang.mixin(new Relationship(), {
			m_typeNode: PRODUCT_COMPLIANCE_ANALYSIS_PARAMETER_VALUE,
			m_query: query,
		});

		const priorityStore = ctx.view.createHeadingNamesStore(ctx, field, null, countryWithAnalysisTypeRel);
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, countryRel, null, { priorityStore: priorityStore });

		return store;
	},

	getUsageStore(field: Field, analysisTypeFieldValue: string): StoreNames {
		const ctx = this.getContext();

		const query = new Query()
			.Attr(PARAMETER_TYPE, xml.OP_EQUALS, "ProductComplianceAnalysisParameterType:Usage")
			.RefAttr("AnalysisTypes", xml.OP_EQUALS, analysisTypeFieldValue);

		const preferredUsage = lang.mixin(new Relationship(), {
			m_addPaths: [lang.mixin(new AddPath(), {
				m_path: "Child:ParentProduct/Child:DefaultComplianceAnalysisUsages",
			})],
			m_typeNode: PRODUCT_COMPLIANCE_ANALYSIS_PARAMETER_VALUE,
			m_query: query,
		});

		const otherUsages = lang.mixin(new Relationship(), {
			m_typeNode: PRODUCT_COMPLIANCE_ANALYSIS_PARAMETER_VALUE,
			m_query: query,
		});

		const priorityStore = ctx.view.createHeadingNamesStore(ctx, field, null, preferredUsage);
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, otherUsages, null, { priorityStore: priorityStore, defaultToPriorityOnly: false });

		return store;
	},

	/**
	 * Returns true if DefaultComplianceAnalysisUsages refmap has a single valid default usage
	 */
	hasSingleUsageInRefMap(defaultComplianceAnalysisUsages: Object): boolean {
		if (!_.isEmpty(defaultComplianceAnalysisUsages) && Object.keys(defaultComplianceAnalysisUsages).length >= 1) {
			const usageCount = Object.values(defaultComplianceAnalysisUsages).reduce((count, value) => count + (value != csi.EMPTY_URL ? 1 : 0), 0);
			return usageCount === 1;
		}
		return false;
	},

	/**
	 * Returns the single valid default usage RequestAnalysisType from the
	 * DefaultComplianceAnalysisUsages refmap
	 */
	getSingleUsageFromRefMap(defaultComplianceAnalysisUsages: Object): string {
		for (const requestAnalysisType in defaultComplianceAnalysisUsages) {
			if (defaultComplianceAnalysisUsages.hasOwnProperty(requestAnalysisType) && defaultComplianceAnalysisUsages[requestAnalysisType] != csi.EMPTY_URL) {
				return requestAnalysisType;
			}
		}
	},

	/**
	 * Returns true if the inputted RequestAnalysisType is in the ComplianceAnalysisTypes
	 * of the parentNode settings/config
	 */
	isAnalysisTypeOnBaseProduct(selectedRequestAnalysisType: string): boolean {
		if (!_.isEmpty(this.parentProductNode)) {
			const ctx = this.getContext();
			const parentNodeType = this.parentProductNode?.$Type;

			let complianceAnalysisTypes = null;
			if (parentNodeType === "Food" || parentNodeType === "FormulatedMaterial") {
				const subtype = ctx.cache.getNode(this.parentProductNode?.Subtype);
				const settings = ctx.cache.getNode(subtype?.Settings);
				complianceAnalysisTypes = settings?.ComplianceAnalysisTypes;
			} else if (parentNodeType === "Style" || parentNodeType === "Material") {
				const productType = ctx.cache.getNode(this.parentProductNode?.ProductType);
				const productionConfig = ctx.cache.getNode(productType?.ProductionConfig);
				complianceAnalysisTypes = productionConfig?.ComplianceAnalysisTypes;
			}

			if (!_.isEmpty(complianceAnalysisTypes)) {
				for (const complianceAnalysisTypeCNL of complianceAnalysisTypes) {
					const complianceAnalysisType = ctx.cache.getNode(complianceAnalysisTypeCNL);
					const requestAnalysisType = complianceAnalysisType?.RequestAnalysisType;
					if (selectedRequestAnalysisType === requestAnalysisType) {
						return true;
					}
				}
			}
			return false;
		}
		return false;
	},
});

export = FoodLabelComplianceAnalysisForm;